// ---------------------------------------------------------------------------
// Centralized color token system for tio-reimagined components
//
// Instead of each component defining its own `colorClasses` record, all
// color tokens live here. Components import only the helpers they need.
// ---------------------------------------------------------------------------

/**
 * Superset of all color names used across reimagined components.
 * Individual components may accept a narrower subset via their own type alias.
 */
export type ReimaginedColor =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'cyan'
  | 'red'
  | 'green'
  | 'indigo'
  | 'purple'
  | 'sky'
  | 'pink'
  | 'rose'
  | 'gray'
  | 'orange';

// ---------------------------------------------------------------------------
// Icon tokens — bg + text for icon badges embedded in cards
// ---------------------------------------------------------------------------

export interface IconColorTokens {
  iconBg: string;
  iconText: string;
}

const iconTokens: Record<ReimaginedColor, IconColorTokens> = {
  emerald: {
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconText: 'text-emerald-600 dark:text-emerald-400',
  },
  blue: { iconBg: 'bg-blue-100 dark:bg-blue-900/30', iconText: 'text-blue-600 dark:text-blue-400' },
  violet: {
    iconBg: 'bg-violet-100 dark:bg-violet-900/30',
    iconText: 'text-violet-600 dark:text-violet-400',
  },
  amber: {
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconText: 'text-amber-600 dark:text-amber-400',
  },
  teal: { iconBg: 'bg-teal-100 dark:bg-teal-900/30', iconText: 'text-teal-600 dark:text-teal-400' },
  cyan: { iconBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconText: 'text-cyan-600 dark:text-cyan-400' },
  red: { iconBg: 'bg-red-100 dark:bg-red-900/30', iconText: 'text-red-600 dark:text-red-400' },
  green: {
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    iconText: 'text-green-600 dark:text-green-400',
  },
  indigo: {
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    iconText: 'text-indigo-600 dark:text-indigo-400',
  },
  purple: {
    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
    iconText: 'text-purple-600 dark:text-purple-400',
  },
  sky: { iconBg: 'bg-sky-100 dark:bg-sky-900/30', iconText: 'text-sky-600 dark:text-sky-400' },
  pink: { iconBg: 'bg-pink-100 dark:bg-pink-900/30', iconText: 'text-pink-600 dark:text-pink-400' },
  rose: { iconBg: 'bg-rose-100 dark:bg-rose-900/30', iconText: 'text-rose-600 dark:text-rose-400' },
  gray: { iconBg: 'bg-gray-100 dark:bg-gray-800', iconText: 'text-gray-600 dark:text-gray-400' },
  orange: {
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    iconText: 'text-orange-600 dark:text-orange-400',
  },
};

/**
 * Some components use `/50` opacity for dark icon backgrounds (e.g. stat-card,
 * icon-badge, nav-card). This variant provides that.
 */
const iconTokens50: Record<ReimaginedColor, IconColorTokens> = {
  emerald: {
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
    iconText: 'text-emerald-600 dark:text-emerald-400',
  },
  blue: { iconBg: 'bg-blue-100 dark:bg-blue-900/50', iconText: 'text-blue-600 dark:text-blue-400' },
  violet: {
    iconBg: 'bg-violet-100 dark:bg-violet-900/50',
    iconText: 'text-violet-600 dark:text-violet-400',
  },
  amber: {
    iconBg: 'bg-amber-100 dark:bg-amber-900/50',
    iconText: 'text-amber-600 dark:text-amber-400',
  },
  teal: { iconBg: 'bg-teal-100 dark:bg-teal-900/50', iconText: 'text-teal-600 dark:text-teal-400' },
  cyan: { iconBg: 'bg-cyan-100 dark:bg-cyan-900/50', iconText: 'text-cyan-600 dark:text-cyan-400' },
  red: { iconBg: 'bg-red-100 dark:bg-red-900/50', iconText: 'text-red-600 dark:text-red-400' },
  green: {
    iconBg: 'bg-green-100 dark:bg-green-900/50',
    iconText: 'text-green-600 dark:text-green-400',
  },
  indigo: {
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/50',
    iconText: 'text-indigo-600 dark:text-indigo-400',
  },
  purple: {
    iconBg: 'bg-purple-100 dark:bg-purple-900/50',
    iconText: 'text-purple-600 dark:text-purple-400',
  },
  sky: { iconBg: 'bg-sky-100 dark:bg-sky-900/50', iconText: 'text-sky-600 dark:text-sky-400' },
  pink: { iconBg: 'bg-pink-100 dark:bg-pink-900/50', iconText: 'text-pink-600 dark:text-pink-400' },
  rose: { iconBg: 'bg-rose-100 dark:bg-rose-900/50', iconText: 'text-rose-600 dark:text-rose-400' },
  gray: { iconBg: 'bg-gray-100 dark:bg-gray-800', iconText: 'text-gray-600 dark:text-gray-400' },
  orange: {
    iconBg: 'bg-orange-100 dark:bg-orange-900/50',
    iconText: 'text-orange-600 dark:text-orange-400',
  },
};

/**
 * Look up icon background + text tokens for the given color.
 * Defaults to 'gray' when the color is missing.
 *
 * @param color - The reimagined color name
 * @param opacity - Use '/50' dark opacity variant (default: '/30')
 */
export function getIconTokens(
  color: ReimaginedColor | undefined,
  opacity: '30' | '50' = '30'
): IconColorTokens {
  const map = opacity === '50' ? iconTokens50 : iconTokens;
  return map[color ?? 'gray'] ?? map.gray;
}

// ---------------------------------------------------------------------------
// Value text color — for stat-card, score displays, etc.
// ---------------------------------------------------------------------------

const valueTextTokens: Record<ReimaginedColor, string> = {
  emerald: 'text-emerald-600 dark:text-emerald-400',
  blue: 'text-blue-600 dark:text-blue-400',
  violet: 'text-violet-600 dark:text-violet-400',
  amber: 'text-amber-600 dark:text-amber-400',
  teal: 'text-teal-600 dark:text-teal-400',
  cyan: 'text-cyan-600 dark:text-cyan-400',
  red: 'text-red-600 dark:text-red-400',
  green: 'text-green-600 dark:text-green-400',
  indigo: 'text-indigo-600 dark:text-indigo-400',
  purple: 'text-purple-600 dark:text-purple-400',
  sky: 'text-sky-600 dark:text-sky-400',
  pink: 'text-pink-600 dark:text-pink-400',
  rose: 'text-rose-600 dark:text-rose-400',
  gray: 'text-gray-900 dark:text-white',
  orange: 'text-orange-600 dark:text-orange-400',
};

/**
 * Text color class for an accent/value. Falls back to the provided fallback or
 * 'text-gray-900 dark:text-white'.
 */
export function getValueTextClass(color?: ReimaginedColor, fallback?: string): string {
  if (color) return valueTextTokens[color] ?? fallback ?? 'text-gray-900 dark:text-white';
  return fallback ?? 'text-gray-900 dark:text-white';
}

// ---------------------------------------------------------------------------
// Icon-only text color — no background
// ---------------------------------------------------------------------------

const iconTextOnlyTokens: Record<ReimaginedColor, string> = {
  emerald: 'text-emerald-600 dark:text-emerald-400',
  blue: 'text-blue-600 dark:text-blue-400',
  violet: 'text-violet-600 dark:text-violet-400',
  amber: 'text-amber-600 dark:text-amber-400',
  teal: 'text-teal-600 dark:text-teal-400',
  cyan: 'text-cyan-600 dark:text-cyan-400',
  red: 'text-red-600 dark:text-red-400',
  green: 'text-green-600 dark:text-green-400',
  indigo: 'text-indigo-600 dark:text-indigo-400',
  purple: 'text-purple-600 dark:text-purple-400',
  sky: 'text-sky-600 dark:text-sky-400',
  pink: 'text-pink-600 dark:text-pink-400',
  rose: 'text-rose-600 dark:text-rose-400',
  gray: 'text-gray-500 dark:text-gray-400',
  orange: 'text-orange-600 dark:text-orange-400',
};

/**
 * Icon text color without background — for section-panel icon colors.
 */
export function getIconTextClass(color?: ReimaginedColor): string {
  return iconTextOnlyTokens[color ?? 'gray'] ?? iconTextOnlyTokens.gray;
}

// ---------------------------------------------------------------------------
// Gradient tokens — for highlight-card, cta-banner, nav-card
// ---------------------------------------------------------------------------

export interface GradientTokens {
  gradient: string;
  border: string;
}

/** Paired gradient mappings for cards and banners. */
const gradientTokens: Record<string, GradientTokens> = {
  emerald: {
    gradient: 'from-emerald-50 to-teal-50 dark:from-emerald-950 dark:to-teal-950',
    border: 'border-emerald-100 dark:border-emerald-800',
  },
  teal: {
    gradient: 'from-teal-50 to-cyan-50 dark:from-teal-950 dark:to-cyan-950',
    border: 'border-teal-100 dark:border-teal-800',
  },
  blue: {
    gradient: 'from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950',
    border: 'border-blue-100 dark:border-blue-800',
  },
  indigo: {
    gradient: 'from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950',
    border: 'border-indigo-100 dark:border-indigo-800',
  },
  violet: {
    gradient: 'from-violet-50 to-purple-50 dark:from-violet-950 dark:to-purple-950',
    border: 'border-violet-100 dark:border-violet-800',
  },
  amber: {
    gradient: 'from-amber-50 to-orange-50 dark:from-amber-950 dark:to-orange-950',
    border: 'border-amber-100 dark:border-amber-800',
  },
  pink: {
    gradient: 'from-pink-50 to-rose-50 dark:from-pink-950 dark:to-rose-950',
    border: 'border-pink-100 dark:border-pink-800',
  },
  rose: {
    gradient: 'from-rose-50 to-pink-50 dark:from-rose-950 dark:to-pink-950',
    border: 'border-rose-100 dark:border-rose-800',
  },
};

export function getGradientTokens(color?: string): GradientTokens {
  return gradientTokens[color ?? 'emerald'] ?? gradientTokens.emerald!;
}

// ---------------------------------------------------------------------------
// Nav-card specific tokens (hover effects, gradient variant)
// ---------------------------------------------------------------------------

export interface NavColorTokens {
  bg: string;
  text: string;
  hoverBg: string;
  hoverBorder: string;
  hoverCaret: string;
  gradient: string;
  gradientBorder: string;
}

const navTokens: Record<string, NavColorTokens> = {
  violet: {
    bg: 'bg-violet-100 dark:bg-violet-900/50',
    text: 'text-violet-600 dark:text-violet-400',
    hoverBg: 'group-hover:bg-violet-600 group-hover:text-white',
    hoverBorder: 'hover:ring-violet-300 dark:hover:ring-violet-600',
    hoverCaret: 'group-hover:text-violet-600 dark:group-hover:text-violet-400',
    gradient: 'from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/30',
    gradientBorder: 'border-violet-100 dark:border-violet-800',
  },
  indigo: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/50',
    text: 'text-indigo-600 dark:text-indigo-400',
    hoverBg: 'group-hover:bg-indigo-600 group-hover:text-white',
    hoverBorder: 'hover:ring-indigo-300 dark:hover:ring-indigo-600',
    hoverCaret: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400',
    gradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30',
    gradientBorder: 'border-indigo-100 dark:border-indigo-800',
  },
  teal: {
    bg: 'bg-teal-100 dark:bg-teal-900/50',
    text: 'text-teal-600 dark:text-teal-400',
    hoverBg: 'group-hover:bg-teal-600 group-hover:text-white',
    hoverBorder: 'hover:ring-teal-300 dark:hover:ring-teal-600',
    hoverCaret: 'group-hover:text-teal-600 dark:group-hover:text-teal-400',
    gradient: 'from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30',
    gradientBorder: 'border-teal-100 dark:border-teal-800',
  },
  emerald: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/50',
    text: 'text-emerald-600 dark:text-emerald-400',
    hoverBg: 'group-hover:bg-emerald-600 group-hover:text-white',
    hoverBorder: 'hover:ring-emerald-300 dark:hover:ring-emerald-600',
    hoverCaret: 'group-hover:text-emerald-600 dark:group-hover:text-emerald-400',
    gradient: 'from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30',
    gradientBorder: 'border-emerald-100 dark:border-emerald-800',
  },
  blue: {
    bg: 'bg-blue-100 dark:bg-blue-900/50',
    text: 'text-blue-600 dark:text-blue-400',
    hoverBg: 'group-hover:bg-blue-600 group-hover:text-white',
    hoverBorder: 'hover:ring-blue-300 dark:hover:ring-blue-600',
    hoverCaret: 'group-hover:text-blue-600 dark:group-hover:text-blue-400',
    gradient: 'from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30',
    gradientBorder: 'border-blue-100 dark:border-blue-800',
  },
  amber: {
    bg: 'bg-amber-100 dark:bg-amber-900/50',
    text: 'text-amber-600 dark:text-amber-400',
    hoverBg: 'group-hover:bg-amber-600 group-hover:text-white',
    hoverBorder: 'hover:ring-amber-300 dark:hover:ring-amber-600',
    hoverCaret: 'group-hover:text-amber-600 dark:group-hover:text-amber-400',
    gradient: 'from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30',
    gradientBorder: 'border-amber-100 dark:border-amber-800',
  },
  pink: {
    bg: 'bg-pink-100 dark:bg-pink-900/50',
    text: 'text-pink-600 dark:text-pink-400',
    hoverBg: 'group-hover:bg-pink-600 group-hover:text-white',
    hoverBorder: 'hover:ring-pink-300 dark:hover:ring-pink-600',
    hoverCaret: 'group-hover:text-pink-600 dark:group-hover:text-pink-400',
    gradient: 'from-pink-50 to-rose-50 dark:from-pink-900/30 dark:to-rose-900/30',
    gradientBorder: 'border-pink-100 dark:border-pink-800',
  },
  green: {
    bg: 'bg-green-100 dark:bg-green-900/50',
    text: 'text-green-600 dark:text-green-400',
    hoverBg: 'group-hover:bg-green-600 group-hover:text-white',
    hoverBorder: 'hover:ring-green-300 dark:hover:ring-green-600',
    hoverCaret: 'group-hover:text-green-600 dark:group-hover:text-green-400',
    gradient: 'from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30',
    gradientBorder: 'border-green-100 dark:border-green-800',
  },
  rose: {
    bg: 'bg-rose-100 dark:bg-rose-900/50',
    text: 'text-rose-600 dark:text-rose-400',
    hoverBg: 'group-hover:bg-rose-600 group-hover:text-white',
    hoverBorder: 'hover:ring-rose-300 dark:hover:ring-rose-600',
    hoverCaret: 'group-hover:text-rose-600 dark:group-hover:text-rose-400',
    gradient: 'from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30',
    gradientBorder: 'border-rose-100 dark:border-rose-800',
  },
};

export function getNavTokens(color?: string): NavColorTokens {
  return navTokens[color ?? 'violet'] ?? navTokens.violet!;
}

// ---------------------------------------------------------------------------
// CTA banner text tokens
// ---------------------------------------------------------------------------

export interface CtaTextTokens {
  gradient: string;
  heading: string;
  description: string;
}

const ctaTokens: Record<string, CtaTextTokens> = {
  emerald: {
    gradient:
      'bg-linear-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20',
    heading: 'text-emerald-900 dark:text-emerald-100',
    description: 'text-emerald-700 dark:text-emerald-300',
  },
  teal: {
    gradient: 'bg-linear-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20',
    heading: 'text-teal-900 dark:text-teal-100',
    description: 'text-teal-700 dark:text-teal-300',
  },
  blue: {
    gradient:
      'bg-linear-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20',
    heading: 'text-blue-900 dark:text-blue-100',
    description: 'text-blue-700 dark:text-blue-300',
  },
  violet: {
    gradient:
      'bg-linear-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20',
    heading: 'text-violet-900 dark:text-violet-100',
    description: 'text-violet-700 dark:text-violet-300',
  },
  amber: {
    gradient:
      'bg-linear-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20',
    heading: 'text-amber-900 dark:text-amber-100',
    description: 'text-amber-700 dark:text-amber-300',
  },
  rose: {
    gradient: 'bg-linear-to-r from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20',
    heading: 'text-rose-900 dark:text-rose-100',
    description: 'text-rose-700 dark:text-rose-300',
  },
  indigo: {
    gradient:
      'bg-linear-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
    heading: 'text-indigo-900 dark:text-indigo-100',
    description: 'text-indigo-700 dark:text-indigo-300',
  },
  pink: {
    gradient: 'bg-linear-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20',
    heading: 'text-pink-900 dark:text-pink-100',
    description: 'text-pink-700 dark:text-pink-300',
  },
};

export function getCtaTokens(theme?: string): CtaTextTokens {
  return ctaTokens[theme ?? 'violet'] ?? ctaTokens.violet!;
}

// ---------------------------------------------------------------------------
// Progress bar fill tokens
// ---------------------------------------------------------------------------

export interface ProgressFillTokens {
  solid: string;
  gradient: string;
}

const progressTokens: Record<string, ProgressFillTokens> = {
  emerald: { solid: 'bg-emerald-500', gradient: 'bg-gradient-to-r from-emerald-400 to-teal-500' },
  blue: { solid: 'bg-blue-500', gradient: 'bg-gradient-to-r from-blue-400 to-indigo-500' },
  violet: { solid: 'bg-violet-500', gradient: 'bg-gradient-to-r from-violet-400 to-purple-500' },
  amber: { solid: 'bg-amber-500', gradient: 'bg-gradient-to-r from-amber-400 to-orange-500' },
  teal: { solid: 'bg-teal-500', gradient: 'bg-gradient-to-r from-teal-400 to-cyan-500' },
  red: { solid: 'bg-red-500', gradient: 'bg-gradient-to-r from-red-400 to-rose-500' },
  green: { solid: 'bg-green-500', gradient: 'bg-gradient-to-r from-green-400 to-emerald-500' },
  indigo: { solid: 'bg-indigo-500', gradient: 'bg-gradient-to-r from-indigo-400 to-violet-500' },
  gray: { solid: 'bg-gray-500', gradient: 'bg-gradient-to-r from-gray-400 to-slate-500' },
};

export function getProgressTokens(color?: string): ProgressFillTokens {
  return progressTokens[color ?? 'blue'] ?? progressTokens.blue!;
}

// ---------------------------------------------------------------------------
// Pipeline stage tokens
// ---------------------------------------------------------------------------

export interface PipelineStageTokens {
  bg: string;
  text: string;
  border: string;
}

const pipelineTokens: Record<string, PipelineStageTokens> = {
  violet: {
    bg: 'bg-violet-50 dark:bg-violet-900/30',
    text: 'text-violet-600 dark:text-violet-400',
    border: 'border-violet-200 dark:border-violet-700',
  },
  teal: {
    bg: 'bg-teal-50 dark:bg-teal-900/30',
    text: 'text-teal-600 dark:text-teal-400',
    border: 'border-teal-200 dark:border-teal-700',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
    border: 'border-green-200 dark:border-green-700',
  },
  gray: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-700 dark:text-gray-200',
    border: 'border-gray-200 dark:border-gray-600',
  },
  amber: {
    bg: 'bg-amber-50 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-700',
  },
};

export function getPipelineTokens(color?: string): PipelineStageTokens {
  return pipelineTokens[color ?? 'violet'] ?? pipelineTokens.violet!;
}

// ---------------------------------------------------------------------------
// Empty-state icon tokens (uses softer text tones)
// ---------------------------------------------------------------------------

export interface EmptyStateTokens {
  bg: string;
  text: string;
}

const emptyStateTokens: Record<string, EmptyStateTokens> = {
  gray: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-400 dark:text-gray-500' },
  violet: {
    bg: 'bg-violet-100 dark:bg-violet-900/50',
    text: 'text-violet-400 dark:text-violet-500',
  },
  blue: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-400 dark:text-blue-500' },
  teal: { bg: 'bg-teal-100 dark:bg-teal-900/50', text: 'text-teal-400 dark:text-teal-500' },
  amber: { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-400 dark:text-amber-500' },
  emerald: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/50',
    text: 'text-emerald-400 dark:text-emerald-500',
  },
};

export function getEmptyStateTokens(color?: string): EmptyStateTokens {
  return emptyStateTokens[color ?? 'gray'] ?? emptyStateTokens.gray!;
}

// ---------------------------------------------------------------------------
// Icon badge tokens (light-only, no dark variants)
// ---------------------------------------------------------------------------

export interface IconBadgeTokens {
  bg: string;
  text: string;
}

const iconBadgeTokens: Record<ReimaginedColor, IconBadgeTokens> = {
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-600' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600' },
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  green: { bg: 'bg-green-100', text: 'text-green-600' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
  sky: { bg: 'bg-sky-100', text: 'text-sky-600' },
  pink: { bg: 'bg-pink-100', text: 'text-pink-600' },
  rose: { bg: 'bg-rose-100', text: 'text-rose-600' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
};

export function getIconBadgeTokens(color?: ReimaginedColor): IconBadgeTokens {
  return iconBadgeTokens[color ?? 'gray'] ?? iconBadgeTokens.gray;
}
